import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router, CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
// [[Providers]]
import { CustomerUserService } from '~project/services/panel/customer/user/user.service';
import { environment } from 'src/environments/environment';
// ]

/**
 * Guarda de acesso do painel.
 *
 * A autenticação passou a ser do Spelt: não há mais login no app.iporto.com.br,
 * que era quem distribuía o cookie para todos os painéis. O fluxo agora é:
 *
 *   customer.spelt.com.br → "Acessar produto"
 *     → {PlanCategory.app_url}?spelt_token=…     (60s, uso único)
 *       → este guard troca o token na API da iPORTO
 *         → grava os cookies no domínio compartilhado (.iporto.com.br)
 *           → limpa a URL e segue para a validação normal de perfil
 *
 * Consumir o token aqui — e não numa rota /auth/spelt dedicada — faz o SSO valer
 * para qualquer `app_url` configurada no Spelt, inclusive deep links, sem precisar
 * criar rota por produto.
 *
 * Como o cookie vale para todo o domínio, um SSO em qualquer painel (inclusive os
 * painéis Blade: SMTP, Prospecção, Validador) autentica este também.
 */
@Injectable()
export class AuthGuard implements CanActivate {
  /** Parâmetro que o Spelt usa ao redirecionar. NÃO é `token`. */
  private static readonly TOKEN_PARAM = 'spelt_token';

  constructor(
    private router: Router,
    private userService: CustomerUserService,
    private cookieService: CookieService,
    private http: HttpClient
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const speltToken = route.queryParamMap.get(AuthGuard.TOKEN_PARAM);

    if (speltToken) {
      return this.consumeSpeltToken(speltToken);
    }

    return this.verifyProfile();
  }

  /**
   * Troca o token do Spelt por uma sessão da iPORTO, grava os cookies e recarrega.
   *
   * O reload não é opcional. Os services deste app capturam o token UMA vez, no
   * construtor (`CustomerUserService` faz
   * `api_token: this.cookieService.get('customer_user_tk')`), e são instanciados no
   * bootstrap do Angular — ou seja, antes deste guard rodar. Sem recarregar, eles
   * seguiriam com o valor lido antes do cookie existir e mandariam
   * `Authorization: Bearer ` vazio, levando 401 em toda chamada.
   *
   * Recarregar re-executa o bootstrap com o cookie já gravado — é o equivalente ao
   * 302 que os painéis Blade fazem. Usamos `replace` para o token não ficar no
   * histórico.
   *
   * Falha na troca não expulsa ninguém: seguimos para verifyProfile(), que decide
   * pela sessão que já exista. Cobre o caso de recarregar a página com um token já
   * consumido.
   */
  private consumeSpeltToken(speltToken: string): Observable<boolean> {
    return this.http.post<any>(environment.sso_exchange_url, { token: speltToken }).pipe(
      switchMap((response: any) => {
        const data = response?.data;

        if (!data?.token) {
          return this.verifyProfile();
        }

        this.storeSession(data);
        window.location.replace(this.urlWithoutToken());

        // Navegação atual cancelada — o reload assume daqui.
        return of(false);
      }),
      catchError(() => {
        this.stripTokenFromUrl();

        return this.verifyProfile();
      })
    );
  }

  /** URL atual sem o parâmetro do token. */
  private urlWithoutToken(): string {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete(AuthGuard.TOKEN_PARAM);

      return url.pathname + url.search + url.hash;
    } catch (_) {
      return '/';
    }
  }

  /**
   * Grava a sessão nos cookies compartilhados.
   *
   * São gravados três nomes de propósito: `customer_user_tk` e `customer_user_id`
   * são os que este app lê, e `api_token` é o que os painéis Blade leem. Assim a
   * sessão vale nos dois mundos, independente de por onde o cliente entrou.
   */
  private storeSession(data: any): void {
    const expires = this.expiresInDays(data.expires_at);
    const domain = environment.cookie_domain;

    this.cookieService.set('customer_user_tk', data.token, expires, '/', domain);
    this.cookieService.set('api_token', data.token, expires, '/', domain);

    if (data.user?.id) {
      this.cookieService.set('customer_user_id', String(data.user.id), expires, '/', domain);
    }

    if (data.user) {
      localStorage.setItem('customer_user', JSON.stringify(data.user));
    }
  }

  /**
   * Remove o token da URL sem recarregar.
   *
   * O token do Spelt vale 60 segundos e é de uso único — a validação o destrói.
   * Deixá-lo na barra de endereços faria um F5 tentar consumi-lo de novo.
   * Usamos replaceState para não criar entrada no histórico.
   */
  private stripTokenFromUrl(): void {
    try {
      window.history.replaceState({}, document.title, this.urlWithoutToken());
    } catch (_) {
      // Sem History API disponível, seguir sem limpar é preferível a quebrar o acesso.
    }
  }

  private expiresInDays(expiresAt: string | null): number {
    if (!expiresAt) {
      return 7;
    }

    const diffMs = new Date(expiresAt).getTime() - Date.now();
    const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    return days > 0 ? days : 7;
  }

  private verifyProfile() {
    return this.userService.profile().pipe(
      map((Profile: any) => {
        if (Profile) {
          return true;
        }

        this.redirectToPanel();

        return false;
      }),
      catchError(() => {
        this.redirectToPanel();

        return of(false);
      })
    );
  }

  /** Sem sessão válida, o caminho é o painel do Spelt — não existe mais login local. */
  private redirectToPanel(): void {
    this.logoutUser();

    document.location.href = environment.spelt_panel_url;
  }

  logoutUser() {
    localStorage.clear();
    // Utilização de Cookie
    this.cookieService.delete('customer_user_tk', '/', environment.cookie_domain);
    this.cookieService.delete('customer_user_id', '/', environment.cookie_domain);
    this.cookieService.delete('api_token', '/', environment.cookie_domain);
  }
}
